/* ODDAY Play Zone: 3D balloon dog (three.js, lazy-loaded) + spray wall */
if (!customElements.get('odday-play')) {
  let threePromise;
  const loadThree = (src) => {
    if (window.THREE) return Promise.resolve(window.THREE);
    if (!threePromise) {
      threePromise = new Promise((res, rej) => {
        const s = document.createElement('script');
        s.src = src; s.async = true;
        s.onload = () => res(window.THREE);
        s.onerror = rej;
        document.head.appendChild(s);
      });
    }
    return threePromise;
  };

  class OddayPlay extends HTMLElement {
    connectedCallback() {
      this.pal = (this.dataset.palette || '#BB2921').split(',').map((c) => c.trim()).filter(Boolean);
      this.col = this.pal[0];
      this.hint = this.querySelector('[data-hint]');
      const sw = this.querySelector('[data-swatches]');
      sw.innerHTML = this.pal.map((c, k) => `<button type="button" style="background:${c}" data-c="${c}" class="${k ? '' : 'is-on'}" aria-label="Colour ${c}"></button>`).join('');
      sw.addEventListener('click', (e) => {
        const b = e.target.closest('[data-c]'); if (!b) return;
        sw.querySelectorAll('button').forEach((x) => x.classList.remove('is-on'));
        b.classList.add('is-on'); this.col = b.dataset.c;
      });
      this.querySelectorAll('[data-tab]').forEach((b) => b.addEventListener('click', () => this.tab(b.dataset.tab)));
      this.initWall();
      const io = new IntersectionObserver((es) => {
        if (es.some((e) => e.isIntersecting)) { io.disconnect(); this.init3D(); }
      }, { rootMargin: '200px' });
      io.observe(this);
    }
    tab(t) {
      this.querySelectorAll('[data-tab]').forEach((b) => b.classList.toggle('is-on', b.dataset.tab === t));
      const is3 = t === '3d';
      this.querySelector('[data-three-canvas]').hidden = !is3;
      this.querySelector('[data-wall]').hidden = is3;
      this.querySelector('[data-btns="3d"]').hidden = !is3;
      this.querySelector('[data-btns="wall"]').hidden = is3;
      this.hint.textContent = is3 ? 'Drag to spin · Tap to paint' : 'Drag to spray paint';
      this.hint.style.opacity = 1;
      if (!is3) this.sizeWall();
    }

    /* ---------- 3D ---------- */
    async init3D() {
      let THREE;
      try { THREE = await loadThree(this.dataset.three); } catch (e) {
        this.insertAdjacentHTML('beforeend', '<div class="odp-fallback">The 3D balloon could not load.<br>Try the spray wall!</div>');
        return;
      }
      const cv = this.querySelector('[data-three-canvas]');
      const R = new THREE.WebGLRenderer({ canvas: cv, antialias: true, alpha: true });
      R.setPixelRatio(Math.min(devicePixelRatio, 2));
      R.shadowMap.enabled = true; R.shadowMap.type = THREE.PCFSoftShadowMap;
      const S = new THREE.Scene();
      const C = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
      C.position.set(0, 2.2, 9); C.lookAt(0, 1.4, 0);
      S.add(new THREE.HemisphereLight(0xffffff, 0xf0b8c4, 0.75));
      const k = new THREE.DirectionalLight(0xffffff, 0.9); k.position.set(4, 8, 6); k.castShadow = true; k.shadow.mapSize.set(1024, 1024); S.add(k);
      const r2 = new THREE.DirectionalLight(0xffe6ea, 0.45); r2.position.set(-6, 3, -4); S.add(r2);
      const pl = new THREE.PointLight(0xffffff, 0.6); pl.position.set(0, 4, 5); S.add(pl);
      const ground = new THREE.Mesh(new THREE.CircleGeometry(3.4, 64), new THREE.ShadowMaterial({ opacity: 0.18 }));
      ground.rotation.x = -Math.PI / 2; ground.receiveShadow = true; S.add(ground);
      const dog = new THREE.Group(); S.add(dog);
      const geo = new THREE.SphereGeometry(1, 48, 32);
      const parts = [];
      const bal = (pos, sc, rot) => {
        const m = new THREE.Mesh(geo, new THREE.MeshPhongMaterial({ color: new THREE.Color(this.col), shininess: 140, specular: 0xffffff }));
        m.position.set(...pos); m.scale.set(...sc); if (rot) m.rotation.set(...rot);
        m.castShadow = true; m.userData.base = m.scale.clone(); dog.add(m); parts.push(m);
      };
      bal([0, 1.35, 0], [1.05, 0.38, 0.38]);
      bal([0.95, 1.95, 0], [0.36, 0.72, 0.36], [0, 0, -0.35]);
      bal([1.25, 2.7, 0], [0.4, 0.38, 0.38]);
      bal([1.78, 2.62, 0], [0.5, 0.24, 0.24], [0, 0, -0.1]);
      bal([1.05, 3.05, 0.24], [0.18, 0.48, 0.18], [0.35, 0, 0.5]);
      bal([1.05, 3.05, -0.24], [0.18, 0.48, 0.18], [-0.35, 0, 0.5]);
      bal([0.85, 0.7, 0.28], [0.19, 0.68, 0.19], [0.18, 0, -0.12]);
      bal([0.85, 0.7, -0.28], [0.19, 0.68, 0.19], [-0.18, 0, -0.12]);
      bal([-0.85, 0.7, 0.28], [0.19, 0.68, 0.19], [0.18, 0, 0.12]);
      bal([-0.85, 0.7, -0.28], [0.19, 0.68, 0.19], [-0.18, 0, 0.12]);
      bal([-1.2, 1.85, 0], [0.17, 0.55, 0.17], [0, 0, 0.55]);
      [[0.85, 1.35, 0], [-0.85, 1.35, 0], [1.1, 2.4, 0]].forEach((p) => bal(p, [0.16, 0.16, 0.16]));
      dog.position.x = -0.25;

      const size = () => { const w = this.clientWidth, h = this.clientHeight; R.setSize(w, h, false); C.aspect = w / h; C.position.z = w < 600 ? 11 : 9; C.updateProjectionMatrix(); };
      new ResizeObserver(size).observe(this); size();

      let rotY = -0.5, vel = 0.004, drag = false, lx = 0, moved = 0, inflate = 0, t = 0, visible = true;
      const ray = new THREE.Raycaster(), mv = new THREE.Vector2();
      cv.addEventListener('pointerdown', (e) => { drag = true; lx = e.clientX; moved = 0; cv.setPointerCapture(e.pointerId); });
      cv.addEventListener('pointermove', (e) => { if (!drag) return; const d = e.clientX - lx; lx = e.clientX; moved += Math.abs(d); vel = d * 0.004; rotY += d * 0.01; });
      cv.addEventListener('pointerup', (e) => {
        drag = false;
        if (moved < 6) {
          const b = cv.getBoundingClientRect();
          mv.set(((e.clientX - b.left) / b.width) * 2 - 1, -((e.clientY - b.top) / b.height) * 2 + 1);
          ray.setFromCamera(mv, C);
          const h = ray.intersectObjects(parts)[0];
          if (h) { h.object.material.color.set(this.col); h.object.userData.pop = 1; this.hint.style.opacity = 0; }
        }
      });
      this.querySelector('[data-random]').addEventListener('click', () => parts.forEach((m, i) => setTimeout(() => { m.material.color.set(this.pal[(Math.random() * this.pal.length) | 0]); m.userData.pop = 1; }, i * 40)));
      this.querySelector('[data-inflate]').addEventListener('click', () => { inflate = 1; parts.forEach((m) => m.material.color.set(this.col)); });
      new IntersectionObserver((es) => { visible = es[0].isIntersecting; }).observe(this);

      const loop = () => {
        requestAnimationFrame(loop);
        if (!visible || cv.hidden) return;
        t += 0.016;
        if (!drag) { rotY += vel; vel += (0.004 - vel) * 0.02; }
        dog.rotation.y = rotY; dog.position.y = Math.sin(t * 1.6) * 0.08;
        if (inflate > 0) inflate -= 0.02;
        const sIn = 1 + Math.sin(Math.max(0, inflate) * Math.PI) * 0.18;
        parts.forEach((m) => {
          const p = m.userData.pop || 0, s = sIn * (1 + Math.sin(p * Math.PI) * 0.22), b = m.userData.base;
          m.scale.set(b.x * s, b.y * s, b.z * s);
          if (p > 0) m.userData.pop = Math.max(0, p - 0.06);
        });
        R.render(S, C);
      };
      loop();
    }

    /* ---------- spray wall ---------- */
    initWall() {
      const wall = (this.wall = this.querySelector('[data-wall]'));
      this.wx = wall.getContext('2d');
      let on = false, sp = { x: 0, y: 0 };
      const pos = (e) => { const b = wall.getBoundingClientRect(); return { x: e.clientX - b.left, y: e.clientY - b.top }; };
      wall.addEventListener('pointerdown', (e) => { on = true; wall.setPointerCapture(e.pointerId); sp = pos(e); this.hint.style.opacity = 0; this.spray(sp.x, sp.y); });
      wall.addEventListener('pointerup', () => (on = false));
      wall.addEventListener('pointermove', (e) => {
        if (!on) return;
        const p = pos(e), d = Math.hypot(p.x - sp.x, p.y - sp.y), n = Math.max(1, (d / 6) | 0);
        for (let i = 0; i < n; i++) this.spray(sp.x + ((p.x - sp.x) * i) / n, sp.y + ((p.y - sp.y) * i) / n);
        sp = p;
      });
      this.querySelector('[data-clear]').addEventListener('click', () => { this.wallSized = false; this.sizeWall(true); });
      this.querySelector('[data-stamp]').addEventListener('click', () => {
        const w = wall.width / 2, h = wall.height / 2, x = this.wx;
        x.save(); x.translate(w * (0.2 + Math.random() * 0.6), h * (0.25 + Math.random() * 0.4)); x.rotate((Math.random() - 0.5) * 0.4);
        x.font = `800 ${Math.min(w / 6, 70)}px 'Monument Extended', Unbounded, sans-serif`; x.textAlign = 'center'; x.fillStyle = this.col; x.globalAlpha = 0.9; x.fillText('ODDAY', 0, 0); x.restore();
      });
      this.querySelector('[data-save]').addEventListener('click', () => {
        const a = document.createElement('a'); a.download = 'my-odday-art.png'; a.href = wall.toDataURL('image/png'); a.click();
        window.ODD?.toast?.(`Art saved! Tag ${this.dataset.handle || '@shopodday'}`);
      });
    }
    sizeWall(clear) {
      const w = this.clientWidth, h = this.clientHeight, wall = this.wall, x = this.wx;
      if (this.wallSized && !clear && wall.width === w * 2) return;
      const old = this.wallSized && !clear ? wall.toDataURL() : null;
      wall.width = w * 2; wall.height = h * 2; x.setTransform(2, 0, 0, 2, 0, 0);
      x.fillStyle = '#FBF8F2'; x.fillRect(0, 0, w, h);
      if (old) { const im = new Image(); im.onload = () => x.drawImage(im, 0, 0, w, h); im.src = old; }
      else {
        x.strokeStyle = 'rgba(0,0,0,.05)'; x.lineWidth = 1;
        for (let y = 0; y < h; y += 34) { x.beginPath(); x.moveTo(0, y); x.lineTo(w, y); x.stroke(); for (let xx = ((y / 34) % 2) * 40; xx < w; xx += 80) { x.beginPath(); x.moveTo(xx, y); x.lineTo(xx, y + 34); x.stroke(); } }
      }
      this.wallSized = true;
    }
    spray(px, py) {
      const x = this.wx;
      x.fillStyle = this.col;
      for (let i = 0; i < 34; i++) { const a = Math.random() * 6.283, r = Math.random() ** 1.6 * 22; x.globalAlpha = 0.55; x.fillRect(px + Math.cos(a) * r, py + Math.sin(a) * r, 1.6, 1.6); }
      x.globalAlpha = 0.08; x.beginPath(); x.arc(px, py, 9, 0, 7); x.fill();
      if (Math.random() < 0.03) { x.globalAlpha = 0.8; x.fillRect(px - 1, py, 2.4, Math.random() * 30 + 10); }
      x.globalAlpha = 1;
    }
  }
  customElements.define('odday-play', OddayPlay);
}
